import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
  getExpandedRowModel,
  flexRender,
  getSortedRowModel,
} from '@tanstack/react-table'

import { cn } from '@/lib/utils'
import useGetTransactionByFilter from '@/services/api/cache/useGetTransactionByFilter'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import useGetTransactionCategory from '@/services/api/transaction-category/useGetTransactionCategory'
import useGetVault from '@/services/api/vault/useGetVault'
import {
  ENTITY_TYPES,
  TRANSACTION_DIRECTION,
  TRANSACTION_TYPES,
} from '@/data/constants/constants'
import useNumberFormatter from '@/hooks/useNumberFormatter'
import { Skeleton } from '../ui/skeleton'
import useGetCellTransactions from '@/services/api/cash-flow/transaction/useGetCellTransactions'
import useGetPeriodByOffset from '@/services/api/cache/useGetPeriodByOffset'
import { formatDate } from 'date-fns'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '../ui/DropdownMenu'
import { Button, ButtonText } from '../ui/button'
import {
  ArrowPathRoundedSquareIcon,
  EllipsisHorizontalIcon,
  EllipsisVerticalIcon,
  PencilSquareIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/16/solid'

import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'

import useDeleteTransaction from '@/services/api/cash-flow/transaction/useDeleteTransaction'
import useDeleteTransactionSeries from '@/services/api/cash-flow/transaction-series/useDeleteTransactionSeries'
import useActualiseTransaction from '@/services/api/cash-flow/transaction/useActualiseTransaction'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/Popover'
import { Calendar } from '../ui/Calendar'
import useChangeTransactionDate from '@/services/api/cash-flow/transaction/useChangeTransactionDate'
import { Badge } from '@/components/ui/Badge'

const columnHelper = createColumnHelper()

function isTodayOrPast(dateStr: string): boolean {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const txDate = new Date(dateStr)
  txDate.setHours(0, 0, 0, 0)
  return txDate <= today
}

function ColumnHeader({ children, className, ...rest }) {
  return (
    <div
      className={cn(
        ['tw-font-medium tw-text-primary -mx-4 bg-gray-low px-4'],
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  )
}

function CategoryName({ id }: { id: number }) {
  const { data, isLoading } = useGetTransactionCategory({ id })

  if (isLoading) return <div>Loading...</div>

  return data?.name ?? 'Unknown'
}

function VaultName({ id }: { id: number }) {
  const { data, isLoading } = useGetVault({ vaultId: id })
  if (isLoading) return <div>Loading...</div>
  return data?.name ?? 'Unknown'
}

function TransactionDate({ transaction }: { transaction: any }) {
  const { action, status } = useChangeTransactionDate()

  const [openDatePicker, setOpenDatePicker] = useState(false)
  const [openWarning, setOpenWarning] = useState(false)

  const [selectedDate, setSelectedDate] = useState(
    new Date(transaction?.transaction_date),
  )

  const applyDateChange = async () => {
    const date = formatDate(selectedDate, 'yyyy-MM-dd')
    await action({ id: transaction.id, transactionDate: date })
    setOpenWarning(false)
    setOpenDatePicker(false)
  }

  return (
    <div>
      <Popover open={openDatePicker} onOpenChange={setOpenDatePicker}>
        <PopoverTrigger>
          <span
            onClick={() => setOpenWarning(true)}
            className="cursor-pointer text-primary underline"
          >
            {' '}
            {formatDate(
              new Date(transaction?.transaction_date),
              'd MMM yyyy',
            )}{' '}
          </span>
        </PopoverTrigger>

        <PopoverContent className="w-auto p-0">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={setSelectedDate}
            initialFocus
          />
          <div className="px-4 pb-4 text-right">
            <Button
              loading={status.status === 'pending'}
              onClick={applyDateChange}
            >
              Update
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
// Main Component <<<<<<<<<<<>>>>>>>>>>>>>>>>>>>>>>>
// Main Component <<<<<<<<<<<>>>>>>>>>>>>>>>>>>>>>>>
// Main Component <<<<<<<<<<<>>>>>>>>>>>>>>>>>>>>>>>
export default function TransactionListTable() {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const { vaultId, categoryId } = useParams()
  const [searchParams] = useSearchParams()
  const offset = searchParams.get('offset')

  const { data: period, status: periodStatus } = useGetPeriodByOffset({
    offset,
  })

  const {
    data: transactions,
    isLoading: isLoadingTransactions,
    isFetching,
  } = useGetCellTransactions({
    entityType: vaultId ? 'vaults' : 'transaction_categories',
    entityId: vaultId ? Number(vaultId) : Number(categoryId),
    start: period?.start,
    end: period?.end,
    enabled: periodStatus === 'success',
  })

  const { data: category, isLoading: isLoadingCategory } =
    useGetTransactionCategory({ id: categoryId })

  const formatter = useNumberFormatter({})

  const [columnVisibility, setColumnVisibility] = useState({
    In: true,
    Out: true,
  })

  const isLoading = isLoadingCategory || isLoadingTransactions || isFetching

  useEffect(() => {
    if (category?.direction === 1) {
      setColumnVisibility({
        In: true,
        Out: false,
      })
    }

    if (category?.direction === -1) {
      setColumnVisibility({
        In: false,
        Out: true,
      })
    }
  }, [category])

  function navigateToEdit({
    series = false,
    transaction,
  }: {
    series: boolean
    transaction: any
  }) {
    const p = new URLSearchParams(searchParams)
    p.set('editSeries', series ? 'true' : 'false')
    p.set(
      'type',
      transaction.transaction_type === TRANSACTION_TYPES.TRANSFER
        ? 'transfer'
        : transaction.destination_type === TRANSACTION_DIRECTION.MONEY_IN
          ? 'in'
          : transaction.destination_type === TRANSACTION_DIRECTION.MONEY_OUT
            ? 'out'
            : 'undefined',
    )

    navigate({
      pathname: `/transaction-list/${vaultId ? `vaults/${vaultId}` : categoryId ? `categories/${categoryId}` : 'undefined'}/transactions/${transaction.id}/edit`,
      search: p.toString(),
    })
    // navigate(
    //   `/transaction-list/${base}/${id}/transactions/${transaction?.id}/edit?type=${type}&editSeries=${series}&offset=${transaction?.offset}`,
    // )
  }

  const columns = useMemo(
    () => [
      columnHelper.accessor('transaction_date', {
        header: ({ table }) => (
          <ColumnHeader
            className={`group flex h-full  items-center   py-2  text-txt-secondary`}
          >
            Transaction Date
          </ColumnHeader>
        ),
        enableSorting: true,
        cell: ({ row, getValue }) => (
          <div className="flex items-center gap-1.5">
            <TransactionDate
              key={row.original.id}
              transaction={row.original}
            />
            {row.original?.series_id && (
              <ArrowPathRoundedSquareIcon
                className="size-3 shrink-0 text-txt-secondary"
                title={row.original?.series_recurrence}
              />
            )}
          </div>
        ),
        // footer: (info) => info.column.id,
      }),
      columnHelper.accessor('from_to', {
        header: ({ table }) => (
          <ColumnHeader
            className={`flex h-full items-center justify-start  py-2 text-left text-txt-secondary`}
          >
            From/To
          </ColumnHeader>
        ),
        enableSorting: true,
        cell: ({ row, getValue }) => (
          <div className="flex items-center justify-start gap-1">
            {categoryId ? (
              <>{row.original.vault_name}</>
            ) : (
              <>
                {row.original.transaction_type ===
                  TRANSACTION_TYPES.STANDARD && (
                  <>{row.original.destination_name}</>
                )}
              </>
            )}

            {row.original.transaction_type === TRANSACTION_TYPES.TRANSFER && (
                <>{row.original.vault_id === row.original.vault_from
                    ? row.original.to.entity_name
                    : row.original.from.entity_name
                }</>
            )}
          </div>
        ),
        // footer: (info) => info.column.id,
      }),
      columnHelper.accessor('description', {
        header: ({ table }) => (
          <ColumnHeader
            className={` flex h-full items-center justify-start py-2 text-txt-secondary`}
          >
            Transaction Description
          </ColumnHeader>
        ),
        enableSorting: true,
        cell: ({ row, getValue }) => {
          const desc = getValue() as string | undefined
          return (
            <div className="flex w-full items-center gap-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="min-w-0 flex-1 truncate">{desc}</div>
                  </TooltipTrigger>
                  {desc && <TooltipContent>{desc}</TooltipContent>}
                </Tooltip>
              </TooltipProvider>
              {row.original?.behaviour === 'EstimateOnly' && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge
                        variant="secondary"
                        className="ml-auto shrink-0 cursor-default px-1 py-0 text-[10px]"
                      >
                        EST
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>Estimate Only — actual not recorded</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          )
        },
        // footer: (info) => info.column.id,
      }),

      columnHelper.accessor('In', {
        header: ({ table }) => (
          <ColumnHeader
            className={`flex h-full items-center justify-end py-2 text-txt-secondary`}
          >
            In
          </ColumnHeader>
        ),
        enableSorting: true,
        enableHiding: true,
        cell: ({ row }) => {
          const { transaction_type, amount, vault_id, vault_to, is_refund, behaviour, transaction_date, estimated_amount } = row.original
          const amt = Number(amount)
          const isDualColumn = !!vaultId
          const showEstimate = behaviour === 'EstimateOnly' && isTodayOrPast(transaction_date) && estimated_amount != null
          const estAmt = Number(estimated_amount ?? amt)
          const estLine = showEstimate && estAmt > 0
            ? <span className="block text-right text-xs text-txt-secondary">est. {formatter(Math.abs(estAmt))}</span>
            : null

          // Transfers: amount > 0 means vault received money
          if (transaction_type === TRANSACTION_TYPES.TRANSFER) {
            if (vault_id === vault_to) {
              return <div className="inline-grid w-full justify-end"><span>{formatter(Math.abs(amt))}</span>{estLine}</div>
            }
            return <div className="inline-grid w-full justify-end">-</div>
          }

          if (isDualColumn) {
            // Dual column (vault view): show in In if money came INTO the vault (amount > 0)
            // Also show for zeroed EstimateOnly transactions with positive estimate
            const showIn = amt > 0 || (showEstimate && estAmt > 0)
            if (!showIn) return <div className="inline-grid w-full justify-end">-</div>
            const display = formatter(amt > 0 ? amt : 0)
            if (is_refund) {
              return (
                <div className="inline-grid w-full justify-end">
                  <TooltipProvider><Tooltip>
                    <TooltipTrigger asChild>
                      <span className="cursor-default">{display}<span className="ml-0.5 text-xs text-txt-secondary">*</span></span>
                    </TooltipTrigger>
                    <TooltipContent>Refund</TooltipContent>
                  </Tooltip></TooltipProvider>
                  {estLine}
                </div>
              )
            }
            return <div className="inline-grid w-full justify-end"><span>{display}</span>{estLine}</div>
          } else {
            // Single column (category view, In column = MONEY_IN category)
            // Normal: positive. Refund: negative with asterisk.
            const display = is_refund ? formatter(-Math.abs(amt)) : formatter(Math.abs(amt))
            if (is_refund) {
              return (
                <div className="inline-grid w-full justify-end">
                  <TooltipProvider><Tooltip>
                    <TooltipTrigger asChild>
                      <span className="cursor-default">{display}<span className="ml-0.5 text-xs text-txt-secondary">*</span></span>
                    </TooltipTrigger>
                    <TooltipContent>Refund</TooltipContent>
                  </Tooltip></TooltipProvider>
                  {estLine}
                </div>
              )
            }
            return <div className="inline-grid w-full justify-end"><span>{display}</span>{estLine}</div>
          }
        },
        footer: (info) => {
          const isDualColumn = !!vaultId
          const total = info.table.getFilteredRowModel().rows.reduce((acc, row) => {
            const { transaction_type, amount, vault_id, vault_to, is_refund } = row.original
            const amt = Number(amount)
            if (transaction_type === TRANSACTION_TYPES.TRANSFER) {
              return acc + (vault_id === vault_to ? Math.abs(amt) : 0)
            }
            if (isDualColumn) {
              return acc + (amt > 0 ? amt : 0)
            } else {
              // Net: normal adds, refund subtracts
              return acc + (is_refund ? -Math.abs(amt) : Math.abs(amt))
            }
          }, 0)
          return <div className="text-right">{formatter(total)}</div>
        },
      }),
      columnHelper.accessor('Out', {
        header: ({ table }) => (
          <ColumnHeader
            className={`flex h-full items-center justify-end py-2 text-txt-secondary`}
          >
            Out
          </ColumnHeader>
        ),
        enableSorting: true,
        enableHiding: true,
        cell: ({ row }) => {
          const { transaction_type, amount, vault_id, vault_from, is_refund, behaviour, transaction_date, estimated_amount } = row.original
          const amt = Number(amount)
          const isDualColumn = !!vaultId
          const showEstimate = behaviour === 'EstimateOnly' && isTodayOrPast(transaction_date) && estimated_amount != null
          const estAmt = Number(estimated_amount ?? amt)
          const estLine = showEstimate && estAmt < 0
            ? <span className="block text-right text-xs text-txt-secondary">est. {formatter(Math.abs(estAmt))}</span>
            : null

          // Transfers: amount < 0 means vault sent money out
          if (transaction_type === TRANSACTION_TYPES.TRANSFER) {
            if (vault_id === vault_from) {
              return <div className="inline-grid w-full justify-end"><span>{formatter(Math.abs(amt))}</span>{estLine}</div>
            }
            return <div className="inline-grid w-full justify-end">-</div>
          }

          if (isDualColumn) {
            // Dual column (vault view): show in Out if money left the vault (amount < 0)
            // Also show for zeroed EstimateOnly transactions with negative estimate
            const showOut = amt < 0 || (showEstimate && estAmt < 0)
            if (!showOut) return <div className="inline-grid w-full justify-end">-</div>
            const display = formatter(Math.abs(amt < 0 ? amt : 0))
            if (is_refund) {
              return (
                <div className="inline-grid w-full justify-end">
                  <TooltipProvider><Tooltip>
                    <TooltipTrigger asChild>
                      <span className="cursor-default">{display}<span className="ml-0.5 text-xs text-txt-secondary">*</span></span>
                    </TooltipTrigger>
                    <TooltipContent>Refund</TooltipContent>
                  </Tooltip></TooltipProvider>
                  {estLine}
                </div>
              )
            }
            return <div className="inline-grid w-full justify-end"><span>{display}</span>{estLine}</div>
          } else {
            // Single column (category view, Out column = MONEY_OUT category)
            // Normal: positive. Refund: negative with asterisk.
            const display = is_refund ? formatter(-Math.abs(amt)) : formatter(Math.abs(amt))
            if (is_refund) {
              return (
                <div className="inline-grid w-full justify-end">
                  <TooltipProvider><Tooltip>
                    <TooltipTrigger asChild>
                      <span className="cursor-default">{display}<span className="ml-0.5 text-xs text-txt-secondary">*</span></span>
                    </TooltipTrigger>
                    <TooltipContent>Refund</TooltipContent>
                  </Tooltip></TooltipProvider>
                  {estLine}
                </div>
              )
            }
            return <div className="inline-grid w-full justify-end"><span>{display}</span>{estLine}</div>
          }
        },
        footer: (info) => {
          const isDualColumn = !!vaultId
          const total = info.table.getFilteredRowModel().rows.reduce((acc, row) => {
            const { transaction_type, amount, vault_id, vault_from, is_refund } = row.original
            const amt = Number(amount)
            if (transaction_type === TRANSACTION_TYPES.TRANSFER) {
              return acc + (vault_id === vault_from ? Math.abs(amt) : 0)
            }
            if (isDualColumn) {
              return acc + (amt < 0 ? Math.abs(amt) : 0)
            } else {
              // Net: normal adds, refund subtracts
              return acc + (is_refund ? -Math.abs(amt) : Math.abs(amt))
            }
          }, 0)
          return <div className="text-right">{formatter(total)}</div>
        },
      }),

      columnHelper.display({
        id: 'actions',
        header: ({ table }) => (
          <ColumnHeader className="flex h-full items-center py-2" />
        ),
        enableSorting: false,
        cell: ({ row }) =>
          row.original?.transaction_type !== TRANSACTION_TYPES.BALANCE ? (
            <div className="flex items-center justify-end opacity-0 transition-opacity group-hover/row:opacity-100">
              <Button
                variant="ghost"
                iconOnly
                onClick={() =>
                  navigateToEdit({ series: false, transaction: row.original })
                }
              >
                <PencilSquareIcon className="size-4" />
              </Button>
              <ActionDropdown transaction={row.original} />
            </div>
          ) : null,
      }),
    ],
    [vaultId, categoryId],
  )

  const table = useReactTable({
    data: transactions,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      columnVisibility,
    },
    onColumnVisibilityChange: setColumnVisibility,
    // initialState: {
    //   columnVisibility: {
    //     In: true,
    //     Out: true,
    //   },
    // },
    // getSubRows: (row) => row.toVaults,
  })

  if (isLoading)
    return (
      <div className="flex w-full flex-col gap-4">
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
      </div>
    )
  return (
    <div
      className={cn(
        'transaction-list-table overflow-auto rounded-md border transition-all data-[busy]:pointer-events-none data-[busy]:animate-pulse data-[busy]:opacity-50',
      )}
    >
      <Table className="overflow-auto-">
        <TableHeader className="sticky top-0 z-50">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className="h-[60px]">
              {headerGroup.headers.map((header, index) => {
                return (
                  <TableHead
                    key={header.id}
                    data-index={index}
                    className={cn(
                      'border-none- bg-white',
                      header.column.id === 'transaction_date' && 'w-[175px]',
                      (header.column.id === 'In' || header.column.id === 'Out') && 'w-[140px]',
                      header.column.id === 'actions' && 'w-[72px]',
                    )}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                )
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => {
              // const getCellColorClassx = (entityType, direction) => {
              //   switch (entityType) {
              //     case ENTITY_TYPES.VAULT_GROUP:
              //       return 'bg-[#E4F1FF]'
              //     case ENTITY_TYPES.MONEY_IN_HEADER:
              //       return 'bg-green-lowest font-medium'
              //     case ENTITY_TYPES.MONEY_OUT_HEADER:
              //       return 'bg-purple-lowest font-medium'
              //     case ENTITY_TYPES.TRANSACTION_CATEGORY_GROUP:
              //       // return direction === -1 ? 'bg-[#F7F2FE]' : 'bg-[#E5F4E9]'
              //       return direction === -1
              //         ? 'bg-[#F5EEFA] font-medium'
              //         : 'bg-[#E5F4E9] font-medium'
              //     case ENTITY_TYPES.TRANSACTION_CATEGORY:
              //       return direction === -1 ? 'bg-[#FAF5FE]' : 'bg-[#EEF7F0]'
              //     default:
              //       return ''
              //   }
              // }

              // function getCellColorClass() {

              // }

              // const cellColorClass = getCellColorClass(
              //   row.original?.entity_type,
              //   row.original?.direction,
              // )
              return (
                <TableRow
                  key={row.id}
                  className={cn(
                    'group/row relative min-h-[52px]',
                    row.original?.behaviour === 'EstimateOnly' &&
                    isTodayOrPast(row.original.transaction_date)
                      ? 'opacity-60'
                      : '!opacity-100',
                    // Cancelled (shadow) rows: the aggregates keep their estimate
                    // but the actual is zero — show them struck through.
                    row.original?.is_shadow &&
                      'text-txt-secondary line-through opacity-60',
                  )}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={`group/cell border-none- relative bg-white transition-all group-hover/row:bg-opacity-15 [&:has([data-negative])]:bg-yellow-lowest [&:has([data-tr-in])]:bg-[#EEF7F0] [&:has([data-tr-out])]:bg-[#FAF5FE]`}
                      data-first-col={
                        cell.column.getIsFirstColumn() ? true : undefined
                      }
                    >
                      <div
                        data-tr-in={
                          row.original.destination_type ===
                          TRANSACTION_DIRECTION.MONEY_IN
                            ? 'true'
                            : undefined
                        }
                        data-tr-out={
                          row.original.destination_type ===
                          TRANSACTION_DIRECTION.MONEY_OUT
                            ? 'true'
                            : undefined
                        }
                        className="hellothere"
                        data-first-col={
                          cell.column.getIsFirstColumn() ? true : undefined
                        }
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </div>
                    </TableCell>
                  ))}
                </TableRow>
              )
            })
          ) : (
            <TableRow>
              <TableCell className=" text-center">No results.</TableCell>
            </TableRow>
          )}
        </TableBody>
        <TableFooter className="sticky bottom-0">
          {/* <tfoot> */}
          {table.getFooterGroups().map((footerGroup) => (
            <TableRow
              key={footerGroup.id}
              className="group/row relative h-[52px] !opacity-100"
            >
              {footerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  className="bg-white- w-[156px]- border-none-"
                  onClick={header.column.getToggleSortingHandler()}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.footer,
                        header.getContext(),
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
          {/* </tfoot> */}
        </TableFooter>
      </Table>
    </div>
  )
}

function ActionDropdown({ transaction }) {
  const { action: deleteT, status: deleteStatus } = useDeleteTransaction()
  const { action: deleteS, status: deleteSeriesStatus } = useDeleteTransactionSeries()
  const { action: actualise, status: actualiseStatus } = useActualiseTransaction({ id: transaction.id })
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [showDeleteSeriesConfirmation, setShowDeleteSeriesConfirmation] =
    useState(false)
  const [showActualiseModal, setShowActualiseModal] = useState(false)
  const [actualiseAmount, setActualiseAmount] = useState<number>(
    Math.abs(transaction?.estimated_amount ?? transaction?.amount ?? 0),
  )

  async function handleDelete({ deleteAll }) {
    if (deleteAll){
      await deleteS({id: transaction.id})
      setShowDeleteSeriesConfirmation(false)
    }else {
      await deleteT({transaction})
      setShowDeleteConfirmation(false)
    }
  }

  async function handleActualise() {
    await actualise({ actual_amount: actualiseAmount })
    setShowActualiseModal(false)
  }

  return (
    <>
      <Dialog
        open={showDeleteConfirmation}
        onOpenChange={setShowDeleteConfirmation}

        // modal={true}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Transaction</DialogTitle>
            <DialogClose>
              <XMarkIcon />
            </DialogClose>
          </DialogHeader>

          <DialogBody>
            <p>Are you sure you want to delete this transaction?</p>
          </DialogBody>

          <DialogFooter>
            <Button
              type="button"
              variant="destructive"
              onClick={() => handleDelete({ deleteAll: false })}
            >
              Delete
            </Button>

            <DialogClose asChild>
              <Button type="button" variant="secondary">
                Cancel
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showDeleteSeriesConfirmation}
        onOpenChange={setShowDeleteSeriesConfirmation}
        // key={transaction.id}
        // modal={true}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Entire Series</DialogTitle>
            <DialogClose>
              <XMarkIcon />
            </DialogClose>
          </DialogHeader>

          <DialogBody>
            <p>
              Are you sure you want to delete this entire series of
              transactions?
            </p>
          </DialogBody>

          <DialogFooter>
            <Button
              type="button"
              variant="destructive"
              onClick={() => handleDelete({ deleteAll: true })}
            >
              Delete
            </Button>

            <DialogClose asChild>
              <Button type="button" variant="secondary">
                Cancel
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Actualise modal */}
      <Dialog open={showActualiseModal} onOpenChange={setShowActualiseModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Actualise Transaction</DialogTitle>
            <DialogClose>
              <XMarkIcon />
            </DialogClose>
          </DialogHeader>

          <DialogBody>
            <p className="mb-4 text-sm text-txt-secondary">
              Confirm this transaction happened. You can adjust the amount if
              the actual differs from the estimate.
            </p>
            <label className="mb-1 block text-sm font-medium">Amount</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={actualiseAmount}
              onChange={(e) => setActualiseAmount(parseFloat(e.target.value) || 0)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </DialogBody>

          <DialogFooter>
            <Button
              type="button"
              loading={actualiseStatus.isPending}
              onClick={handleActualise}
            >
              Confirm
            </Button>

            <DialogClose asChild>
              <Button type="button" variant="secondary">
                Cancel
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DropdownMenu modal={false}>
        {transaction?.transaction_type !== TRANSACTION_TYPES.BALANCE && (
          <DropdownMenuTrigger>
            <Button
              type="button"
              iconOnly
              variant="ghost"
              className="bg-transparent"
            >
              <EllipsisHorizontalIcon />
              {/* <EllipsisVerticalIcon /> */}
            </Button>
          </DropdownMenuTrigger>
        )}

        <DropdownMenuContent side="bottom" align="end">
          {/* ACTUALISE */}
          {transaction?.can_actualise && (
            <DropdownMenuItem>
              <Button
                onClick={() => {
                  setActualiseAmount(
                    Math.abs(transaction?.estimated_amount ?? transaction?.amount ?? 0),
                  )
                  setShowActualiseModal(true)
                }}
                variant="ghost"
              >
                <ButtonText>Actualise</ButtonText>
              </Button>
            </DropdownMenuItem>
          )}

          {/* DELETE */}

          {!transaction?.series_id && (
            <DropdownMenuItem>
              <Button
                onClick={() => setShowDeleteConfirmation(true)}
                variant="ghost"
                className="!text-txt-error"
              >
                <TrashIcon className="!text-txt-error" />
                <ButtonText>Delete</ButtonText>
              </Button>
            </DropdownMenuItem>
          )}

          {/* Delete Series */}
          {transaction?.series_id && (
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="pl-3">
                <div className="flex items-center gap-2 text-txt-error">
                  <TrashIcon className="size-4 !text-txt-error" />

                  <ButtonText>Delete</ButtonText>
                </div>
              </DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent>
                  <DropdownMenuItem
                    onClick={() => setShowDeleteConfirmation(true)}
                  >
                    This only
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setShowDeleteSeriesConfirmation(true)}
                  >
                    This and Following
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  )
}
